'use client';

import { useId } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

export interface DateRangeValue {
  from?: string;
  to?: string;
}

export interface DateRangePickerProps {
  label?: string;
  value?: DateRangeValue;
  onChange: (value: DateRangeValue | undefined) => void;
  fromLabel?: string;
  toLabel?: string;
  disabled?: boolean;
  /** Overrides the generated id prefix for the two inputs. */
  idPrefix?: string;
}

export function DateRangePicker({
  label,
  value,
  onChange,
  fromLabel,
  toLabel,
  disabled,
  idPrefix,
}: DateRangePickerProps) {
  const generatedId = useId();
  const prefix = idPrefix ?? `date-range-${generatedId}`;
  const range = value ?? {};
  const isInvalidRange = Boolean(range.from && range.to && range.from > range.to);

  const update = (patch: Partial<DateRangeValue>) => {
    const next = { ...range, ...patch };
    onChange(next.from || next.to ? next : undefined);
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <TextField
        id={`${prefix}-from`}
        label={fromLabel ?? (label ? `${label} from` : 'From')}
        type="date"
        size="small"
        value={range.from ?? ''}
        onChange={(e) => update({ from: e.target.value || undefined })}
        // A native date input always paints its own mask, so the label must
        // stay shrunk or it sits on top of that mask while the field is empty.
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ minWidth: 160 }}
        error={isInvalidRange}
        disabled={disabled}
      />
      <TextField
        id={`${prefix}-to`}
        label={toLabel ?? (label ? `${label} to` : 'To')}
        type="date"
        size="small"
        value={range.to ?? ''}
        onChange={(e) => update({ to: e.target.value || undefined })}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ minWidth: 160 }}
        error={isInvalidRange}
        helperText={isInvalidRange ? '"To" must be on or after "From"' : undefined}
        disabled={disabled}
      />
    </Box>
  );
}

export default DateRangePicker;
